import { useEffect, useRef, type ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useLocation } from 'react-router-dom'

import Layout from '../../components/layout/Layout'
import { Badge } from '../../components/ui'
import {
  cashflowV2QueryKeys,
  useDataSourceConfig,
  useSnapshotStatus,
} from './api'
import AskElizaDrawer from './components/AskElizaDrawer'
import {
  CashflowV2Loading,
  CashflowV2Unconfigured,
} from './components/PageStates'

const CONFIG_PATH = '/cashflow-v2/admin/configuration'

export default function CashflowV2Layout({ children }: { children: ReactNode }) {
  const location = useLocation()
  const queryClient = useQueryClient()
  const configQuery = useDataSourceConfig()
  const snapshotQuery = useSnapshotStatus()
  const previousPublishedRun = useRef<string | null>(null)

  const isConfigPage = location.pathname === CONFIG_PATH

  // While the config probe is still running on the very first paint, show a
  // lightweight spinner rather than blinking the pages' own loading skeletons
  // followed by an "unconfigured" message.
  const showInitialLoader = configQuery.isLoading && !configQuery.data

  // Gate: only force the setup CTA when the resolver explicitly says we have
  // no usable data source. Pages already render with the env fallback / saved
  // connector happily otherwise. The config page itself must stay reachable.
  const mode = configQuery.data?.mode
  const shouldBlock = !isConfigPage && mode === 'unconfigured'

  // Don't show the assistant until a data source is usable — otherwise it
  // has nothing to ground its answers in.
  const canUseAssistant = !shouldBlock && !showInitialLoader

  useEffect(() => {
    const publishedRun = snapshotQuery.data?.published_run_id ?? null
    if (
      previousPublishedRun.current &&
      publishedRun &&
      previousPublishedRun.current !== publishedRun
    ) {
      queryClient.invalidateQueries({
        queryKey: cashflowV2QueryKeys.all,
        predicate: query => query.queryKey[1] !== 'snapshot-status',
      })
    }
    previousPublishedRun.current = publishedRun
  }, [queryClient, snapshotQuery.data?.published_run_id])

  const snapshotState = snapshotQuery.data?.state
  const snapshotVariant =
    snapshotState === 'ready'
      ? 'success'
      : snapshotState === 'refreshing'
        ? 'info'
        : snapshotState === 'failed_refresh'
          ? 'warning'
          : 'secondary'
  const snapshotLabel =
    snapshotState === 'ready'
      ? 'Data ready'
      : snapshotState === 'refreshing'
        ? 'Refreshing data'
        : snapshotState === 'failed_refresh'
          ? 'Using last good data'
          : 'Live-source fallback'

  return (
    <Layout>
      <div className="h-full overflow-auto bg-gray-50 dark:bg-dark-bg">
        <div className="max-w-[1600px] mx-auto px-6 py-6">
          {!showInitialLoader && !shouldBlock && snapshotState ? (
            <div className="mb-3 flex items-center justify-end gap-2">
              <Badge variant={snapshotVariant}>{snapshotLabel}</Badge>
              {snapshotQuery.data?.published_at ? (
                <span className="text-micro text-gray-500 dark:text-gray-400">
                  Published {new Date(snapshotQuery.data.published_at).toLocaleString()}
                </span>
              ) : null}
            </div>
          ) : null}
          {showInitialLoader ? (
            <CashflowV2Loading label="Checking Cashflow V2 data source…" />
          ) : shouldBlock ? (
            <CashflowV2Unconfigured />
          ) : (
            children
          )}
        </div>
      </div>
      {canUseAssistant ? <AskElizaDrawer /> : null}
    </Layout>
  )
}
